import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'modules/auth/contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import ServerMaintenance from 'shared/components/ServerMaintenance';

const ProtectedRoute = ({ children, requiredRoles = [] }) => {

    const {
        isAuthenticated,
        loading,
        serverDown,
        retryAuth,
        user,
        userRole,
        effectiveRole
    } = useAuth();

    const location = useLocation();

    // ================= LOADING =================
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // ================= SERVER UNREACHABLE =================
    // Distinct from "not authenticated" - the token is still valid, the
    // server itself just can't be reached right now. See AuthContext's
    // isServerUnreachable/serverDown for why this must never fall through
    // to the redirect-to-/login branch below.
    if (serverDown) {
        return <ServerMaintenance onRetry={retryAuth} />;
    }

    // ================= NOT AUTH =================
    if (!isAuthenticated) {
        return (
            <Navigate
                to="/login"
                state={{ from: location }}
                replace
            />
        );
    }

    // ================= FORCE PASSWORD =================
    if (user?.must_change_password && location.pathname !== '/change-password') {
        return <Navigate to="/change-password" replace />;
    }

    // ================= ROLE NORMALIZATION =================
    // Gate on the *effective* (base) role so custom roles inherit the access
    // level of the built-in role they are based on.
    const role = String(
        effectiveRole ||
        userRole ||
        user?.role_name ||
        user?.role
    ).toUpperCase();

    const allowedRoles = (requiredRoles || []).map(r => r.toUpperCase());

    // ================= ROLE CHECK =================
    if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
        return <Navigate to="/unauthorized" replace />;
    }

    // ================= SUCCESS =================
    return children;
};

export default ProtectedRoute;